import {createContext, ReactNode, useContext, useEffect, useState} from 'react';
import {useCookies} from 'react-cookie';
import {AxiosError} from 'axios';
import {ApiError, UserAlreadyExistsError, UserAnswersDTO, UserDTO} from '../types';
import {useGlobalContext} from './GlobalContext';

type UserContextType = {
  user: UserDTO;
  updateUser: (user: UserDTO) => void;
  createUser: (contactID: string, firstName: string, lastName: string, email: string, phone: string) => Promise<UserDTO | null>;
  login: (contactID: string) => void;
  answers: UserAnswersDTO[];
}

interface UserContextProviderProps {
  children: ReactNode;
}

const defaultUser: UserDTO = {
  contactID: '',
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  babies: false,
  communityMember: false,
  kids12: false,
  dogsOwners: '',
  vacationsType: '',
  travelevel: '',
  createdAt: new Date(),
};

export const UserContext = createContext<UserContextType>({
  user: defaultUser,
  updateUser: () => null,
  createUser: async () => null,
  login: async () => {},
  answers: []
});

export const useUserContext = () => useContext(UserContext);

const cookieFlashyContactID = 'fls_id';

export const UserContextProvider = ({children}: UserContextProviderProps) => {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<UserDTO>(defaultUser);
  const [answers, setAnswers] = useState<UserAnswersDTO[]>([]);
  const [cookies, setCookie, removeCookie] = useCookies([cookieFlashyContactID]);
  const {api} = useGlobalContext();

  const getUser = async (contactID: string) => {
    try {
      const user = await api.getUserByContactID(contactID);
      setUser(user);
      const answers = await api.getAnswers(contactID);
      setAnswers(answers);
    } catch (err) {
      setUser(defaultUser);
      setAnswers([]);
      if (err instanceof ApiError) {
        console.error(err.response?.data);
      } else {
        console.error(err);
      }
    }
  };

  const updateUser = async (user: UserDTO) => setUser(user);

  const createUser = async (contactID: string, firstName: string, lastName: string, email: string, phone: string) => {
    try {
      return await api.createNewUser(
        contactID,
        firstName,
        lastName,
        email,
        phone,
      );
    } catch (e) {
      if (e instanceof AxiosError && e.response?.data.errorCode === -22) {
        throw new UserAlreadyExistsError();
      } else {
        throw e;
      }
    }
  };

  const login = (contactID: string) => {
    removeCookie(cookieFlashyContactID);
    setCookie(cookieFlashyContactID, contactID);
  };

  useEffect(() => {
    if (cookies.fls_id) {
      setLoading(true);
      getUser(cookies.fls_id)
        .finally(() => setLoading(false));
    } else {
      setUser(defaultUser);
      setLoading(false);
    }
    // eslint-disable-next-line
  }, [cookies.fls_id]);

  return loading
    ? <div>...loading</div>
    : (
      <UserContext.Provider value={{user, updateUser, createUser, login, answers}}>
        {children}
      </UserContext.Provider>
    );
};